import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Outlet } from 'react-router-dom';
import { Layout } from './components/Layout';
import { RenderFailureReporter } from './telemetry/renderFailureReporter';
import { RouteViewReporter } from './telemetry/RouteViewReporter';
import { UnhandledExceptionReporter } from './telemetry/unhandledExceptionReporter';
import {} from './telemetry/clientTelemetryContract';
/**
 * Per-route lazy chunks. The data router's `lazy:` callback (react-router-dom v6.4+,
 * data routers only) returns `{ Component, ErrorBoundary, loader, action, ... }` from a
 * dynamic `import()`. Each screen becomes its own per-route chunk; the screen modules are
 * NEVER imported statically here, so they do not enter the consuming shell's MAIN bundle.
 *
 * Why this matters: a shell that imports `routes` (web-ui, desktop, mini-app, mobile-web)
 * gets the SAME main bundle composition regardless of which route it visits. Statically
 * importing the screen modules here would land all four screens in EVERY shell's main
 * chunk — including shells where only ONE screen is reachable per SPA load (chat-side
 * `/agents` flows on TG-family shells, web-ui's section replacing `/agents` entirely).
 *
 * The lazy pattern is the same one web-ui's section already uses at
 * `apps/web-ui/src/routes/admin-tenant/routes.ts` (`{ path: '/agents', lazy: () => import('./entry').then(m => ({ Component: m.SectionList })) }`).
 * Pinned by `test/routes-does-not-eagerly-import-screens.test.ts` ("If `routes.tsx` ever
 * grows a static import of a screen, the test reds and the main bundle is on notice").
 *
 * WEBUI-ONLY LOADING SURFACE (web!598 product review, `554796787:b735a3060df921cd`):
 * react-router v6.28 data routers render ONLY `fallbackElement` (or `null` if
 * omitted) while a route's `lazy:` callback is unresolved. The four shells
 * (`apps/web-ui`, `apps/desktop`, `apps/mini-app`, `apps/mobile`) wire
 * `fallbackElement={<RouteFallback />}` at every `<RouterProvider>` so the user
 * sees a localized loading surface, not an empty container. `RouteFallback` lives
 * at `packages/app-core/src/components/RouteFallback.tsx`; deterministic coverage
 * in `apps/web-ui/test/route-fallback-on-lazy.test.tsx` plus the tightened
 * `packages/app-core/test/routes-provider-contract.test.tsx` test that asserts the
 * `route-fallback` testid is visible BEFORE awaiting the chunk (an empty tree is
 * no longer acceptable here).
 */
const lazyChatScreen = () => import('./screens/ChatScreen').then((m) => ({ Component: m.ChatScreen }));
const lazyAgentsScreen = () => import('./screens/AgentsScreen').then((m) => ({ Component: m.AgentsScreen }));
const lazyAgentCreateScreen = () => import('./screens/AgentCreateScreen').then((m) => ({ Component: m.AgentCreateScreen }));
const lazySettingsScreen = () => import('./screens/SettingsScreen').then((m) => ({ Component: m.SettingsScreen }));
/**
 * SHELL-WRAP SEAM (ADR-079): the route table is the shared artifact; the ROUTER
 * FACTORY is per-shell. web-ui uses history routing (createBrowserRouter — do
 * NOT hash-route the web surface); file:// / custom-scheme shells (Tauri
 * desktop, Telegram mini-app webview) build their entry with
 * createHashRouter/createMemoryRouter over these SAME routes.
 */
// ONE root element, deliberately shared by all three root-building sites below
// (plain `routes` and both shell-extension helpers): the render-failure tap
// wraps the chrome, so every shell that imports this table gets the producer
// without per-shell wiring. The binding is pinned by
// `test/renderFailureReporter.test.tsx` ("route-table binding"), which asserts
// every exported table's root element IS this reporter — reverting any of the
// three sites to bare `<Layout />` reds it.
// ADR-141 D1.1's route-view producer rides the SAME seam, for the same reason:
// it needs `useMatches()` (so it must be inside the router) and it must exist on
// every shell that mounts these routes without per-shell wiring. It is inert
// unless the shell declared `config.analytics`, so mounting it everywhere arms
// nothing — see `RouteViewReporter`'s docblock. Pinned by the same
// route-table-binding test.
// `UnhandledExceptionReporter` rides the same seam for the same "no per-shell
// wiring" reason, though it does not need `useMatches()` itself (v1 ships with no
// route context — see its own docblock); mounting it here rather than requiring
// each shell's entry point to remember a global `window` listener is the point.
const layoutRoot = (_jsxs(RenderFailureReporter, { children: [_jsx(RouteViewReporter, {}), _jsx(UnhandledExceptionReporter, {}), _jsx(Layout, {})] }));
const layoutChildren = [
    {
        path: '/',
        lazy: lazyChatScreen,
        handle: { telemetryRoute: 'chat' },
    },
    {
        path: '/agents',
        lazy: lazyAgentsScreen,
        handle: { telemetryRoute: 'agents' },
    },
    {
        path: '/agents/new',
        lazy: lazyAgentCreateScreen,
        handle: { telemetryRoute: 'agent-create' },
    },
    {
        path: '/settings',
        lazy: lazySettingsScreen,
        handle: { telemetryRoute: 'settings' },
    },
];
export const routes = [{ element: layoutRoot, children: layoutChildren }];
/**
 * Shell route extension (same ADR-079 seam, extended by the architect navigation
 * call 2026-08-18T15:44Z): a shell with surface-local screens — web-ui's
 * admin-tenant section — appends them as children of the shared Layout route:
 * one SPA, one chrome, one session. Returns a NEW table built from the same
 * `layoutChildren` const, so `routes` itself is never mutated and a shell that
 * imports it plain (desktop, mini-app, mobile) cannot grow a route it never
 * registered. Pass a `lazy` route for a code-split section
 * (`{ path: '…', lazy: () => import('…') }`) — the chunk stays out of every
 * table that does not extend with it.
 *
 * Both tables share the ONE root built here (Layout + children); if the root
 * ever grows more props, this factory is the single place to add them.
 */
export function extendRoutes(extra) {
    return [{ element: layoutRoot, children: [...layoutChildren, ...extra] }];
}
/**
 * Shell route REPLACEMENT (architect ruling 2026-08-18T16:06Z, on Dmitry's
 * /agents path preference DM 452): web-ui's admin-tenant section mounts AT
 * `/agents`, which the chat-side children already occupy. This strips the
 * shared children at `paths` and returns a table with `extra` in their place —
 * web-ui-local: the shared `routes` const is untouched, so TG-family shells
 * (mini-app / desktop / mobile web) keep the chat-side `/agents` flows.
 *
 * Throws (does not silently no-op) when a path in `paths` matches no shared
 * child: a typo here would leave BOTH the chat route and the section route
 * claiming the same path, react-router would rank them equal and take the
 * first — the section would be silently unreachable. Loud beats shadowed.
 */
export function replaceRoutesByPath(paths, extra) {
    const replaceSet = new Set(paths);
    const kept = layoutChildren.filter((child) => !(typeof child.path === 'string' && replaceSet.delete(child.path)));
    if (replaceSet.size > 0) {
        throw new Error(`replaceRoutesByPath: no shared route at path(s): ${[...replaceSet].join(', ')}`);
    }
    return [{ element: layoutRoot, children: [...kept, ...extra] }];
}
/**
 * Shell route SIBLING composition (the third shape, added for desktop's
 * `/bridge`): appends top-level routes BESIDE the shared table. EVERY form is
 * covered (architect round-5 correction — wrapping only `element` let
 * `Component`/`lazy`/element-less siblings ship without the tap):
 *  - `element` → wrapped;
 *  - `Component` → wrapped and REPLACED by the wrapped element (React Router
 *    would otherwise render Component, ignoring element);
 *  - `lazy` → the thunk is wrapped so the resolved `Component`/`element`
 *    mounts inside the reporter (the chunk stays lazy; only its content is
 *    wrapped);
 *  - neither (pathless layout) → `element: <Reporter><Outlet/></Reporter>`:
 *    children keep rendering through the Outlet, now inside the tap.
 */
export function composeRoutes(extra) {
    return [...routes, ...extra.map(wrapRouteWithTap)];
}
// The destructure OMITS Component/lazy/element from the passthrough fields:
// React Router gives Component precedence over element, so a surviving
// Component would bypass the tap entirely (architect round-8 blocker 3).
// A lazy route resolving only loader/action still gets the implicit-Outlet
// tap — loader errors are covered by the router tap, render by this.
function wrapRouteWithTap(route) {
    const { Component, lazy, element, ...rest } = route;
    // The STATIC renderer is preserved across the lazy merge: a route with a
    // static element/Component AND a lazy data thunk keeps its wrapped renderer
    // while the thunk contributes only its data (loader/action) — stripping the
    // static renderer here lost valid routes (architect round-9 blocker 3).
    let staticRenderer;
    if (Component != null) {
        const Inner = Component;
        staticRenderer = _jsx(RenderFailureReporter, { children: _jsx(Inner, {}) });
    }
    else if (element !== undefined) {
        staticRenderer = _jsx(RenderFailureReporter, { children: element });
    }
    if (lazy !== undefined) {
        return {
            ...rest,
            element: staticRenderer ?? _jsx(RenderFailureReporter, { children: _jsx(Outlet, {}) }),
            lazy: async () => {
                const resolved = await lazy();
                if (resolved.Component != null) {
                    const Inner = resolved.Component;
                    return { ...resolved, Component: tapComponent(Inner) };
                }
                if (resolved.element != null) {
                    return { ...resolved, element: _jsx(RenderFailureReporter, { children: resolved.element }) };
                }
                // loader/action-only thunk: no renderer of its own — the static one
                // (or the implicit Outlet) stays.
                return resolved;
            },
        };
    }
    if (staticRenderer !== undefined) {
        return { ...rest, element: staticRenderer };
    }
    return { ...rest, element: _jsx(RenderFailureReporter, { children: _jsx(Outlet, {}) }) };
}
function tapComponent(Inner) {
    return function TappedComponent(props) {
        return _jsx(RenderFailureReporter, { children: _jsx(Inner, { ...props }) });
    };
}
//# sourceMappingURL=routes.js.map